/**
 * Sheet 43, Settings › Data retention (mission 10 WP16 T090 to T096; FR-017, FR-022, FR-024, NFR-011,
 * NFR-012; C-002, C-008, C-009, C-015; D.2, D.3, D.6, D.7, D.8, A.3, A.5, NF.9, UC-04; spec amendments 3, 18,
 * 19, 26; OR-15; squad S13, U16, U17, U26, U31; research §1.27 "Sheet 43"; style guide §8 "Settings").
 *
 * One `retentionQuery()` on open: its first page carries the window and its `terms`, every period the page
 * states (FR-022), and older pages of the runs table come by cursor. One identity per tenant, so there is no
 * Member summary or Auditor view (43/1's `deferred:none` halves). The h1 is the register 17 crumb, then the
 * UTC note once per view, then the cards in the drawn order:
 * - RetentionControl (callouts 2 to 4): the window, its provenance, the notes, retention 0 and the save bar,
 *   with the shortening dialog;
 * - Class1Card (5), the Class 2 card with the shared Class2Statement (6), EncryptionStatement (7);
 * - RetentionRunsTable (8), ExportCard (9) and DeleteAccountCard with its dialog (10, 11).
 *
 * The shell's billing banner shows while dunning or read-only; then the retention-0 warning renders inline
 * in the control's card, so no third band stacks (§7.5; U31). The credits read is the chip's own observer of
 * `['credits']`, so it adds no request. Every route here answers while read-only (`allowed(reasonPrivacy)`),
 * so nothing on the page is gated. Desktop only (C-015): the runs table's container scrolls, never the page.
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useTenant } from '@/components/trellis/AuthGate'
import { Band } from '@/components/trellis/Band'
import { Class2Statement } from '@/components/trellis/Class2Statement'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError } from '@/lib/api'
import type { RetentionWindow } from '@/lib/api/retention'
import { copyText } from '@/lib/provisional-copy'
import { creditsQuery, retentionQuery } from '@/lib/queries'
import { SettingsLayout } from '../SettingsLayout'
import { Class1Card } from './Class1Card'
import { DeleteAccountCard } from './DeleteAccountCard'
import { EncryptionStatement } from './EncryptionStatement'
import { ExportCard } from './ExportCard'
import { RetentionControl } from './RetentionControl'
import { RetentionRunsTable } from './RetentionRunsTable'

/** The shell's BillingBanner speaks in these states (sheet 51/7, 51/8). */
function useShellBand(): boolean {
  const { data } = useQuery(creditsQuery())
  const state = data?.billing?.state
  return state === 'dunning' || state === 'read_only'
}

export default function DataRetention() {
  const query = useInfiniteQuery(retentionQuery())
  const pages = query.data?.pages

  let body
  if (pages?.[0] !== undefined) {
    body = <Sections first={pages[0]} pages={pages} query={query} />
  } else if (query.isError) {
    body =
      query.error instanceof ApiError ? (
        <ProblemBand problem={query.error.problem} />
      ) : (
        <Band kind="blocked">
          <p>{copyText('genericError')}</p>
        </Band>
      )
  } else {
    body = (
      <>
        <Skeleton className="h-48 rounded-lg" />
        <Skeleton className="h-24 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </>
    )
  }

  return (
    <SettingsLayout>
      <h1 className="text-h1 text-navy-950">{copyText('crumb.data')}</h1>
      {body}
    </SettingsLayout>
  )
}

interface SectionsProps {
  first: RetentionWindow
  pages: readonly RetentionWindow[]
  query: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => Promise<unknown> }
}

function Sections({ first, pages, query }: SectionsProps) {
  const shellBand = useShellBand()
  const tenant = useTenant()
  const { terms } = first
  return (
    <>
      <p className="text-caption text-navy-600">{copyText('retentionTimezoneNote')}</p>
      <RetentionControl window={first} shellBand={shellBand} />
      <Class1Card />
      <Card>
        <CardHeader>
          <CardTitle>{copyText('class2Title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Class2Statement years={terms.class2_years} />
        </CardContent>
      </Card>
      <EncryptionStatement />
      <RetentionRunsTable
        runs={pages.flatMap((p) => p.runs)}
        hasNextPage={query.hasNextPage}
        loadingNext={query.isFetchingNextPage}
        onNextPage={() => void query.fetchNextPage()}
      />
      <ExportCard request={first.export_request} fulfilmentDays={terms.fulfilment_days} />
      <DeleteAccountCard
        legalName={tenant.data?.legal_name}
        days={terms.fulfilment_days}
        years={terms.class2_years}
        exportOpen={first.export_request !== null}
      />
    </>
  )
}
